import { CloseOutlined } from "@ant-design/icons";
import { DatePicker, message } from "antd";
import fr_FR from "antd/lib/date-picker/locale/fr_FR";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import categoryIcon from "../../../assets/img/categories.svg";
import coinsIcon from "../../../assets/img/coins.svg";
import comptaIcon from "../../../assets/img/compta.svg";
import graphicIcon from "../../../assets/img/graphic.svg";
import budgetIcon from "../../../assets/img/BUDGET.svg";
import projectIcon from "../../../assets/img/project.svg";
import releveIcon from "../../../assets/img/relevé.svg";
import sigmaIcon from "../../../assets/img/icons/sigma.svg";
import syntheseIcon from "../../../assets/img/SYNTHESE.svg";
import exportIcon from "../../../assets/img/export.svg";
import { ReactComponent as NextIcon } from "../../../assets/img/icons/next.svg";
import { ReactComponent as PrevIcon } from "../../../assets/img/icons/prev.svg";
import {
  setMonthList,
  setRegroupmentValue,
} from "../../../data/slices/categories";
import {
  handelEndMonth,
  handelStartMonth,
  handleActiveObjectif,
  handleActiveSynthesis,
  handleEndMonthSynthesis,
  handleMode,
  handleOpenOverlay,
  handleSelectedMonths,
  handleSelectedMonthsSynthesis,
  handleStartMonthSynthesis,
  setSubMenuName,
} from "../../../data/slices/layout";
import { closeModal, openModal } from "../../../data/slices/modals";
import { openListObjectiveDialog } from "../../../data/slices/objectives";
import { updateProject } from "../../../data/slices/project";
import { PATH_HOME } from "../../../routes/paths";
import { generateMonthList } from "../../../utilities/filters";
import { renderFiscalYearEndPickerPanel } from "../../../utilities/fiscalYearPickerPanel";
import {
  formatFiscalYearEndLabel,
  getExerciceBounds,
  getPeriodPresetFromCurrentMonth,
  normalizeDisplayedPeriodPreset,
} from "../../../utilities/periodPresets";
import Overlay from "../../Overlay";
import HeaderOverlay from "../../Project/Header";
import "./index.scss";

const TRESO_VIEWS = {
  categories: { id: 0, name: "Catégories", icon: categoryIcon },
  listing: {
    id: 1,
    name: "Relevé",
    icon: releveIcon,
    route: "listing",
  },
  graph: { id: 2, name: "Graphique", icon: graphicIcon, route: "graph" },
};

const getGraphPeriod = () => {
  const current = dayjs().startOf("month");
  return [current.subtract(6, "month"), current.add(1, "month")];
};

function ProjectReadyActionsModal({ open, onCancel, data }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { project } = useSelector((state) => state.project);
  const projectId = data?.projectId || project?.id;
  const [fiscalPickerOpen, setFiscalPickerOpen] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);

  const closeReadyActions = () => {
    dispatch(closeModal("project-ready-actions-modal"));
    setFiscalPickerOpen(false);
  };

  const showSoldesPointes = () => {
    if (!projectId) return;
    dayjs.locale("fr");
    dispatch(
      openModal("show-pointed-sums-modal", {
        month: dayjs().format("MMMM YYYY"),
        projectId,
        lockStatus: "cannot be locked",
      })
    );
  };

  const applyDisplayedPeriod = (dates, regroupmentValue = 1) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return;
    dispatch(handelStartMonth(dates[0]));
    dispatch(handelEndMonth(dates[1]));
    dispatch(handleSelectedMonths());
    dispatch(setMonthList(generateMonthList(dates[0], dates[1])));
    dispatch(setRegroupmentValue(regroupmentValue));
  };

  const applySynthesePeriod = (dates) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return;
    dispatch(handleStartMonthSynthesis(dates[0]));
    dispatch(handleEndMonthSynthesis(dates[1]));
    dispatch(handleSelectedMonthsSynthesis());
    dispatch(handleActiveSynthesis(true));
  };

  const goToCategories = () => {
    if (!projectId) return closeReadyActions();

    const isProjectHome =
      location.pathname === PATH_HOME.project.view(projectId);

    closeReadyActions();
    dispatch(handleMode("treso"));
    dispatch(handleActiveObjectif(false));
    dispatch(handleActiveSynthesis(false));
    dispatch(setSubMenuName(TRESO_VIEWS.categories));
    // Always re-apply the default saved period — including when already on
    // the categories page after the user manually changed the displayed dates.
    applyDisplayedPeriod(
      getPeriodPresetFromCurrentMonth(
        normalizeDisplayedPeriodPreset(project?.displayedPeriodPreset)
      )
    );

    if (!isProjectHome) {
      navigate(PATH_HOME.project.view(projectId));
    }
  };

  const goToReleve3Months = () => {
    closeReadyActions();
    dispatch(handleMode("treso"));
    dispatch(handleActiveObjectif(false));
    dispatch(handleActiveSynthesis(false));
    dispatch(setSubMenuName(TRESO_VIEWS.listing));
    applyDisplayedPeriod(getPeriodPresetFromCurrentMonth("3months"));
    navigate(PATH_HOME.curve.listing(projectId));
  };

  const goToGraph = () => {
    closeReadyActions();
    dispatch(handleMode("treso"));
    dispatch(handleActiveObjectif(false));
    dispatch(handleActiveSynthesis(false));
    dispatch(setSubMenuName(TRESO_VIEWS.graph));
    applyDisplayedPeriod(getGraphPeriod());
    navigate(PATH_HOME.curve.graph(projectId));
  };

  const openSoldesPointes = () => {
    closeReadyActions();
    dispatch(handleMode("treso"));
    showSoldesPointes();
  };

  const applyResultatExerciceSetup = async (fiscalYearEnd) => {
    const { start, end } = getExerciceBounds(fiscalYearEnd);
    if (!start?.isValid?.() || !end?.isValid?.()) {
      message.error("Exercice en cours invalide");
      return;
    }

    const startStr = start.format("YYYY-MM");
    const endStr = end.format("YYYY-MM");

    dispatch(handleMode("compta"));
    applyDisplayedPeriod([start, end], 1);
    dispatch(setSubMenuName(TRESO_VIEWS.categories));
    navigate(PATH_HOME.project.view(projectId));

    // Persist synthèse on the exercice period so SyntheseHeader does not
    // reset to the previously saved synthèse range (e.g. Avr–Aoû).
    if (projectId) {
      try {
        await dispatch(
          updateProject({
            id: Number(projectId),
            syntheseDateStart: startStr,
            syntheseDateEnd: endStr,
          })
        ).unwrap();
      } catch (err) {
        // Still apply locally even if persist fails
      }
    }

    applySynthesePeriod([start, end]);
    dispatch(openListObjectiveDialog({ status: true }));
  };

  const handleBudgetSuiviClick = () => {
    if (!project?.fiscalYearEnd) {
      setFiscalPickerOpen(true);
      return;
    }
    closeReadyActions();
    applyResultatExerciceSetup(project.fiscalYearEnd);
  };

  const toggleFiscalPicker = (event) => {
    event?.stopPropagation?.();
    if (savingFiscal) return;
    setFiscalPickerOpen((prev) => !prev);
  };

  const handleSaveFiscalYear = async (value) => {
    if (!value || !project?.id) return;
    const nextValue = value.format("YYYY-MM");
    setSavingFiscal(true);
    try {
      await dispatch(
        updateProject({
          id: Number(project.id),
          fiscalYearEnd: nextValue,
        })
      ).unwrap();
      message.success("Exercice en cours enregistré");
      setFiscalPickerOpen(false);
      closeReadyActions();
      applyResultatExerciceSetup(nextValue);
    } catch (err) {
      message.error("Impossible d'enregistrer l'exercice en cours");
    } finally {
      setSavingFiscal(false);
    }
  };

  const openExport = () => {
    closeReadyActions();
    dispatch(openModal("export-type-modal", { projectId }));
  };

  const changeDossier = () => {
    closeReadyActions();
    sessionStorage.removeItem("lastOpenedProjectId");
    dispatch(handleOpenOverlay(true));
    navigate(PATH_HOME.project.root);
  };

  if (!open) return null;

  return (
    <Overlay show={open}>
      <HeaderOverlay showlogo className="project-ready-actions-overlay">
        <button
          type="button"
          className="project-ready-actions-close"
          onClick={onCancel || closeReadyActions}
          aria-label="Fermer"
        >
          <CloseOutlined />
        </button>

        <div className="project-ready-actions">
          <div className="project-ready-actions__title">
            {project?.projectName || "Projet"}
          </div>

          <div className="project-ready-actions__cards">
            <section className="project-ready-actions-card project-ready-actions-card--treso">
              <div className="project-ready-actions-card__header">
                <img src={coinsIcon} alt="" />
                <h3>Suivre et mettre à jour la trésorerie :</h3>
              </div>
              <div className="project-ready-actions-card__options">
                <button type="button" onClick={goToCategories}>
                  <img src={categoryIcon} alt="" />
                  <span>
                    <span className="project-ready-actions-card__option-prefix">
                      Vue catégories :
                    </span>{" "}
                    Afficher le tableau de trésorerie (Période par défaut)
                  </span>
                </button>
                <button type="button" onClick={goToReleve3Months}>
                  <img src={releveIcon} alt="" />
                  <span>
                    <span className="project-ready-actions-card__option-prefix">
                      Vue relevé 3 mois :
                    </span>{" "}
                    Afficher le relevé de trésorerie (de m-1 à m+1).
                  </span>
                </button>
                <button type="button" onClick={goToGraph}>
                  <img src={graphicIcon} alt="" />
                  <span>
                    <span className="project-ready-actions-card__option-prefix">
                      Vue Graphique :
                    </span>{" "}
                    visualiser l’évolution des disponibilités de trésorerie des
                    6 derniers mois
                  </span>
                </button>
                <button type="button" onClick={openSoldesPointes}>
                  <img src={coinsIcon} alt="" />
                  <span>Voir les derniers soldes de trésorerie pointés</span>
                </button>
              </div>
            </section>

            <section className="project-ready-actions-card project-ready-actions-card--resultat">
              <div className="project-ready-actions-card__header">
                <img src={comptaIcon} alt="" />
                <h3>Contrôler le résultat et le suivi d’un budget :</h3>
              </div>
              <div className="project-ready-actions-card__options">
                {project?.fiscalYearEnd ? (
                  <button type="button" onClick={handleBudgetSuiviClick}>
                    <span className="project-ready-actions-card__icons">
                      <img src={sigmaIcon} alt="" />
                      <img src={budgetIcon} alt="" />
                    </span>
                    <span>
                      Afficher le suivi de l'exercice en cours et un budget
                    </span>
                  </button>
                ) : (
                  <div className="project-ready-actions-card__option-row">
                    <button
                      type="button"
                      className="project-ready-actions-card__option-action"
                      onClick={handleBudgetSuiviClick}
                    >
                      <span className="project-ready-actions-card__icons">
                        <img src={sigmaIcon} alt="" />
                        <img src={budgetIcon} alt="" />
                      </span>
                      <span>
                        Afficher le suivi de l'exercice en cours et un budget
                      </span>
                    </button>
                    <div className="period-affichee-picker-slot period-affichee-fiscal-slot project-ready-actions-fiscal-slot">
                      <button
                        type="button"
                        className="period-affichee-current-btn period-affichee-exercice-btn"
                        disabled={savingFiscal}
                        onClick={toggleFiscalPicker}
                      >
                        {formatFiscalYearEndLabel(project?.fiscalYearEnd)}
                      </button>
                      <DatePicker
                        open={fiscalPickerOpen}
                        onOpenChange={(visible) => {
                          if (!savingFiscal) setFiscalPickerOpen(visible);
                        }}
                        value={null}
                        onChange={handleSaveFiscalYear}
                        picker="month"
                        locale={fr_FR}
                        format="MMM YYYY"
                        allowClear={false}
                        disabled={savingFiscal}
                        className="period-affichee-hidden-picker"
                        dropdownClassName="period-affichee-fiscal-dropdown"
                        panelRender={renderFiscalYearEndPickerPanel}
                        placement="bottomRight"
                        getPopupContainer={() => document.body}
                        superNextIcon={<NextIcon />}
                        superPrevIcon={<PrevIcon />}
                      />
                    </div>
                  </div>
                )}
                <button type="button" onClick={openExport}>
                  <span className="project-ready-actions-card__icons">
                    <img src={syntheseIcon} alt="" />
                    <img
                      src={exportIcon}
                      alt=""
                      className="project-ready-actions-export-icon"
                    />
                  </span>
                  <span>
                    Exporter des états (liste d'écritures, bilan, compte de
                    résultat)
                  </span>
                </button>
              </div>
            </section>
          </div>

          <button
            type="button"
            className="project-ready-actions__change-dossier"
            onClick={changeDossier}
          >
            <img src={projectIcon} alt="" />
            <span>Changer de dossier</span>
          </button>
        </div>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ProjectReadyActionsModal;
